/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import type { FileManagerData, FileManagerRow } from "./fileSystemServer.schema";

/**
 * Shared hook for FileManager logic that works on both web and mobile.
 * Contains: breadcrumbs, search debouncing, file filtering/sorting, path column state.
 *
 * Web-specific logic (SlickGrid, context menus, drag-drop) remains in useFileManagerFiles.
 */

/**
 * The callback slots are declared with METHOD SHORTHAND (`f(x): void`), not property arrows
 * (`f: (x) => void`). A method's parameter is compared bivariantly, so a caller may hand in a
 * handler typed against its own row/state type — which is exactly what every consumer does — while
 * a property arrow would be contravariant and reject all of them. For the same reason the payload
 * members are optional and the parameters are neither optional nor nullable: a `?` or `| null` on
 * the declared parameter (rather than on the property) makes neither direction assignable.
 */
interface UseFileManagerSharedProps {
  setSelectedFolder(folder: FileManagerRow): void;
  fileManagerData?: FileManagerData;
  tempFolder?: FileManagerRow;
  /** `null` is the "no active search" sentinel the debounce state starts in. */
  handleSearchFiles?(query: string | null): void;
  selectedFolder?: FileManagerRow;
  preSearchText?: string;
  onFileLoad(files: FileManagerRow[]): void;
}

function useFileManagerShared({
  setSelectedFolder,
  fileManagerData = {},
  tempFolder = {},
  handleSearchFiles = () => {},
  selectedFolder = {},
  preSearchText = "",
  onFileLoad = () => {},
}: UseFileManagerSharedProps) {
  // Search state
  const [debounced, setDebounced] = useState<string | null>(null);
  const [query, setQuery] = useState<string | null>(preSearchText || null);
  const delay = 300;

  // UI state
  const [showPathColumn, setShowPathColumn] = useState(false);
  const [breadCrumbData, setBreadCrumbData] = useState<FileManagerRow[]>([]);
  const [filesData, setFilesData] = useState<FileManagerRow[]>([]);
  const [isFirstRender, setIsFirstRender] = useState(true);
  // Tracks the previously-selected folder id so we only clear an active search on a genuine
  // user navigation (real folder -> real folder), not on the very first selection.
  const prevSelectedFolderIdRef = useRef<string | undefined>(undefined);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(query), delay);
    return () => clearTimeout(handler);
  }, [query]);

  // Handle pre-populated search text
  useEffect(() => {
    if (preSearchText) {
      setQuery(preSearchText);
    }
  }, [preSearchText]);

  // Call API search when debounced value changes
  useEffect(() => {
    if (debounced === null) {
      return;
    }
    if (debounced === "") {
      setShowPathColumn(false);
    } else {
      setShowPathColumn(true);
    }
    handleSearchFiles(debounced);
  }, [debounced]);

  // Clear search when the folder changes — but only on a genuine user navigation between two real
  // folders. The very first selection (initial mount, or a deep-link Space focus that lands a tick
  // after mount in project "Folders" mode) must NOT wipe a pre-search supplied via the URL
  // (?searchText=&spaceId=), which seeds `query` before that Space is focused.
  useEffect(() => {
    const prevId = prevSelectedFolderIdRef.current;
    prevSelectedFolderIdRef.current = selectedFolder?.id;
    if (prevId !== undefined && prevId !== selectedFolder?.id) {
      setDebounced(null);
      setQuery(null);
    }
    setIsFirstRender(false);
  }, [selectedFolder?.id]);

  // Filter and sort files based on current folder and search query
  useEffect(() => {
    const data = fileManagerData.filteredFiles || fileManagerData.files || [];
    // Every branch below assigns, so no placeholder initialiser.
    let filteredItems: FileManagerRow[];

    if (tempFolder.resourceType === "RESOURCE_TYPE_TRANSMITTAL") {
      filteredItems = _.cloneDeep(data).filter(
        (item: FileManagerRow) =>
          (item.transmittalVersionId === tempFolder.id && item.resourceType !== "RESOURCE_TYPE_TRANSMITTAL") || query,
      );
    } else if (!tempFolder.resourceType) {
      filteredItems = _.cloneDeep(data).filter(
        (item: FileManagerRow) =>
          (item.parentId === tempFolder.id && item.resourceType === "RESOURCE_TYPE_TRANSMITTAL") || query,
      );
    } else {
      filteredItems = _.cloneDeep(data).filter((item: FileManagerRow) => item.parentId === tempFolder.id || query);
    }

    // Sort: folders first (alphabetically), then files (alphabetically)
    const folders = filteredItems.filter(
      (resource: FileManagerRow) => resource.resourceType === "RESOURCE_TYPE_FOLDER",
    );
    const resourceOtherThanFolders = filteredItems.filter(
      (resource: FileManagerRow) => resource.resourceType !== "RESOURCE_TYPE_FOLDER",
    );
    // KNOWN BUG, preserved verbatim: a row with no `name` makes the comparator return `undefined`
    // instead of a number, which leaves the sort order unspecified. Erased assertions only.
    const sortedFolders = folders.sort(
      (a: FileManagerRow, b: FileManagerRow) => a.name?.localeCompare(b.name as string) as number,
    );
    const sortedResources = resourceOtherThanFolders.sort(
      (a: FileManagerRow, b: FileManagerRow) => a.name?.localeCompare(b.name as string) as number,
    );
    const allFiles = [...sortedFolders, ...sortedResources];
    onFileLoad(allFiles);
    setFilesData(allFiles);
  }, [tempFolder?.id, fileManagerData]);

  // Compute breadcrumbs from tempFolder's parentIdArray
  useEffect(() => {
    // Project ("Folders") mode is detected structurally (one Space-root node per Space). The breadcrumb
    // root is then the selected Space itself, not a single "This Space" cwd.
    const spaceRoots = (fileManagerData.files || []).filter((f: FileManagerRow) => f?.isSpaceRoot);
    if (spaceRoots.length) {
      const tempIsSpaceRoot = !!(tempFolder?.isSpaceRoot && tempFolder?.spaceId);
      const spaceRoot = spaceRoots.find((f: FileManagerRow) => f.spaceId === tempFolder?.spaceId);
      const hierarchy: FileManagerRow[] = [];
      if (tempFolder?.parentIdArray?.length) {
        tempFolder.parentIdArray.forEach((id: string) => {
          const obj = fileManagerData.files?.find((item: FileManagerRow) => item.id === id && !item.isSpaceRoot);
          if (obj) hierarchy.push(obj);
        });
      }
      const crumbs: FileManagerRow[] = [];
      if (spaceRoot) crumbs.push(spaceRoot);
      crumbs.push(...hierarchy);
      if (tempFolder?.id && !tempIsSpaceRoot) crumbs.push(tempFolder);
      setBreadCrumbData(crumbs);
      return;
    }

    const hierarchy: FileManagerRow[] = [];
    if (tempFolder.id && tempFolder.parentIdArray?.length) {
      tempFolder.parentIdArray.forEach((id: string) => {
        const obj = fileManagerData.files?.find((item: FileManagerRow) => item.id === id);
        if (obj) {
          hierarchy.push(obj);
        }
      });
    }
    if (tempFolder.id !== "0") {
      hierarchy.push(tempFolder);
    }
    const rootFolder = {
      ...fileManagerData.cwd,
      hasChild: true,
      parentId: null,
      id: "0",
    };
    setBreadCrumbData(hierarchy.length ? [rootFolder, ...hierarchy] : [rootFolder]);
  }, [tempFolder?.id, fileManagerData.cwd]);

  // Navigate to a breadcrumb folder by clicking
  const navigateToBreadcrumb = (folder: FileManagerRow) => {
    setSelectedFolder(folder);
  };

  // Navigate back to parent folder
  const navigateBack = () => {
    if (breadCrumbData.length > 1) {
      const parentFolder = breadCrumbData[breadCrumbData.length - 2];
      // Guarded by `breadCrumbData.length > 1` above, so the index is in range; the `| undefined`
      // is only `noUncheckedIndexedAccess`. Erased.
      setSelectedFolder(parentFolder!);
    }
  };

  return {
    // Search state
    query,
    setQuery,
    debounced,
    setDebounced,
    showPathColumn,
    setShowPathColumn,

    // Files data
    filesData,
    setFilesData,

    // Breadcrumbs
    breadCrumbData,
    setBreadCrumbData,

    // Navigation helpers
    navigateToBreadcrumb,
    navigateBack,

    // Internal state
    isFirstRender,
    setIsFirstRender,
  };
}

export default useFileManagerShared;
